import { z } from 'zod';
import { UuidSchema } from '../_shared/ids.js';
import { UserRoleSchema } from './enums.js';
/**
 * Usuarios del medio. Contratos compartidos entre la API y el panel.
 *
 * Los usuarios **no se borran, se desactivan**: la firma de una nota apunta a su autor
 * (`articles.author_id`, obligatorio y sin cascade), así que un borrado físico falla contra la
 * clave ajena — y debería, porque quién escribió cada nota es parte del registro del medio.
 *
 * La contraseña nunca llega en un DTO. El servidor genera una temporal al dar de alta, la manda
 * por email y la devuelve **una sola vez** en la respuesta del alta; después no existe forma de
 * volver a verla, solo de regenerarla.
 */
export const UsuarioDTO = z
    .object({
    id: UuidSchema,
    nombre: z.string(),
    email: z.string().email(),
    role: UserRoleSchema,
    /** Baja lógica: un usuario inactivo no entra al panel y sus notas siguen firmadas por él. */
    activo: z.boolean(),
    /** `null` mientras no haya entrado nunca: informa si la invitación llegó a destino. */
    ultimoIngreso: z.string().datetime().nullable(),
    /** Sigue con la contraseña temporal: la tiene que cambiar antes de poder trabajar. */
    debeCambiarPassword: z.boolean(),
    creadoEn: z.string().datetime(),
})
    .strict();
export const USUARIO_NOMBRE_MAX = 120;
export const USUARIO_EMAIL_MAX = 200;
export const CrearUsuarioRequest = z
    .object({
    nombre: z.string().trim().min(1).max(USUARIO_NOMBRE_MAX),
    // Se normaliza a minúsculas acá y no solo en el servidor: el email es la identidad con la que
    // se entra, y "Ana@medio.com" y "ana@medio.com" tienen que ser la misma persona.
    email: z.string().trim().toLowerCase().email().max(USUARIO_EMAIL_MAX),
    role: UserRoleSchema,
})
    .strict();
/**
 * Alta desde el backoffice de plataforma. Es el mismo alta, sin `role`: desde ahí se crea el
 * **Administrador** del medio y nada más, y después él da de alta a su redacción desde su panel.
 *
 * El rol no viaja en el body a propósito, en vez de venir y validarse: un campo que el servidor
 * ignora invita a mandarlo igual, y un `role: 'WRITER'` aceptado en silencio crearía un medio sin
 * nadie que pueda administrarlo. Derivado de `CrearUsuarioRequest` para que nombre y email no se
 * puedan desincronizar con el alta del panel.
 */
export const CrearAdminDelMedioRequest = CrearUsuarioRequest.omit({ role: true });
/**
 * El email no se edita: es la identidad con la que se entra y con la que se recupera la cuenta.
 * Cambiarlo es dar de baja a alguien y dar de alta a otro.
 */
export const ActualizarUsuarioRequest = z
    .object({
    nombre: z.string().trim().min(1).max(USUARIO_NOMBRE_MAX).optional(),
    role: UserRoleSchema.optional(),
    activo: z.boolean().optional(),
})
    .strict()
    .refine((v) => Object.values(v).some((campo) => campo !== undefined), {
    message: 'No hay nada para cambiar',
});
/**
 * La contraseña temporal viaja en la respuesta **y** por email, y el panel la muestra una vez.
 * `emailEnviado: false` cuando el SMTP del medio falló: el alta ya está hecha, y quien invitó
 * tiene que poder dictar la contraseña en vez de tener que empezar de nuevo.
 */
export const PasswordTemporalResponse = z
    .object({
    passwordTemporal: z.string(),
    emailEnviado: z.boolean(),
})
    .strict();
export const CrearUsuarioResponse = PasswordTemporalResponse.extend({
    usuario: UsuarioDTO,
}).strict();
/** Códigos de error del dominio que el panel trata de forma específica. */
export const USUARIO_ERROR_CODES = {
    noEncontrado: 'USUARIO_NO_ENCONTRADO',
    /**
     * El email ya lo usa alguien **en la plataforma**, no solo en este medio: el login busca por
     * email global, así que un email repetido en otro medio daría una cuenta con la que nunca se
     * puede entrar. El mensaje no dice de qué medio: eso sería contarle a un cliente sobre otro.
     */
    emailEnUso: 'USUARIO_EMAIL_EN_USO',
    /** Nadie se cambia su propio rol ni se desactiva a sí mismo. */
    esUnoMismo: 'USUARIO_ES_UNO_MISMO',
    /** El medio quedaría sin ningún administrador activo y nadie podría volver a entrar a esto. */
    ultimoAdmin: 'USUARIO_ULTIMO_ADMIN',
};
